import Big, { RoundingMode } from 'big.js';

// 舍入规则 - Big.js的舍入模式
export const ROUND_DOWN = 0 as RoundingMode; // 向零舍入
export const ROUND_HALF_UP = 1 as RoundingMode; // 四舍五入
export const ROUND_HALF_EVEN = 2 as RoundingMode; // 银行家舍入
export const ROUND_UP = 3 as RoundingMode; // 远离零舍入

// 设置Big.js默认精度和舍入模式
Big.DP = 2; // 默认小数位数
Big.RM = ROUND_DOWN; // 默认舍入模式（向零舍入，避免用户余额显示变多）

/**
 * 转化为Number类型，若结果为NaN，则取defaultValue的值
 * @param value 当前值
 * @param defaultValue 默认值
 * @returns number
 */
export function toNum(value: any, defaultValue: number = 0): number {
  const num = typeof value === 'number' ? value : parseFloat(value);
  return Number.isNaN(num) ? defaultValue : num;
}

/**
 * 相加
 * @param n1 第一个数
 * @param n2 第二个数
 * @param dp 保留小数位，默认原小数位
 * @param r 舍入方式，默认向下舍入（round down）
 * @returns number
 */
export function add(n1: any, n2: any, dp?: number, r?: RoundingMode): number {
  const result = new Big(toNum(n1)).plus(new Big(toNum(n2)));
  if (dp !== undefined) {
    const oldRM = Big.RM;
    // 如果指定了 dp 但没有指定 r，默认使用 ROUND_DOWN
    Big.RM = r !== undefined ? r : ROUND_DOWN;
    const rounded = result.round(dp);
    Big.RM = oldRM;
    return Number(rounded.toString());
  }
  return Number(result.toString());
}

/**
 * 相减
 * @param n1 第一个数
 * @param n2 第二个数
 * @param dp 保留小数位，默认原小数位
 * @param r 舍入方式，默认向下舍入（round down）
 * @returns number
 */
export function sub(n1: any, n2: any, dp?: number, r?: RoundingMode): number {
  const result = new Big(toNum(n1)).minus(new Big(toNum(n2)));
  if (dp !== undefined) {
    const oldRM = Big.RM;
    // 如果指定了 dp 但没有指定 r，默认使用 ROUND_DOWN
    Big.RM = r !== undefined ? r : ROUND_DOWN;
    const rounded = result.round(dp);
    Big.RM = oldRM;
    return Number(rounded.toString());
  }
  return Number(result.toString());
}

/**
 * 相乘
 * @param n1 第一个数
 * @param n2 第二个数
 * @param dp 保留小数位，默认原小数位
 * @param r 舍入方式，默认向下舍入（round down）
 * @returns number
 */
export function mul(n1: any, n2: any, dp?: number, r?: RoundingMode): number {
  const result = new Big(toNum(n1)).times(new Big(toNum(n2)));
  if (dp !== undefined) {
    const oldRM = Big.RM;
    // 如果指定了 dp 但没有指定 r，默认使用 ROUND_DOWN
    Big.RM = r !== undefined ? r : ROUND_DOWN;
    const rounded = result.round(dp);
    Big.RM = oldRM;
    return Number(rounded.toString());
  }
  return Number(result.toString());
}

/**
 * 相除
 * @param n1 第一个数
 * @param n2 第二个数
 * @param dp 保留小数位，默认原小数位
 * @param r 舍入方式，默认向下舍入（round down）
 * @returns number
 */
export function div(n1: any, n2: any, dp?: number, r?: RoundingMode): number {
  // 临时提高 Big.DP 以确保计算精度，避免被默认的 Big.DP = 2 限制
  const oldDP = Big.DP;
  if (dp !== undefined && dp > oldDP) {
    Big.DP = dp;
  }
  const result = new Big(toNum(n1)).div(new Big(toNum(n2)));
  if (dp !== undefined) {
    const oldRM = Big.RM;
    // 如果指定了 dp 但没有指定 r，默认使用 ROUND_DOWN
    Big.RM = r !== undefined ? r : ROUND_DOWN;
    const rounded = result.round(dp);
    Big.RM = oldRM;
    Big.DP = oldDP; // 恢复原始 Big.DP
    return Number(rounded.toString());
  }
  Big.DP = oldDP; // 恢复原始 Big.DP
  return Number(result.toString());
}

/**
 * 保留小数位，多余小数位使用舍入方式截取
 * @param num 数值
 * @param dp 保留小数位
 * @param r 舍入方式，默认向下舍入（round down）
 * @returns number
 */
export function toDP(num: any, dp?: number, r?: RoundingMode): number {
  // 临时提高 Big.DP 以确保计算精度，避免被默认的 Big.DP = 2 限制
  const oldDP = Big.DP;
  const big = new Big(toNum(num));
  if (dp !== undefined) {
    if (dp > oldDP) {
      Big.DP = dp;
    }
    const oldRM = Big.RM;
    // 如果指定了 dp 但没有指定 r，默认使用 ROUND_DOWN
    Big.RM = r !== undefined ? r : ROUND_DOWN;
    const rounded = big.round(dp);
    Big.RM = oldRM;
    Big.DP = oldDP; // 恢复原始 Big.DP
    return Number(rounded.toString());
  }
  Big.DP = oldDP; // 恢复原始 Big.DP
  return Number(big.toString());
}

/**
 * 固定小数位，多余小数位使用舍入方式截取，不够则补0填充
 * @param num 数值
 * @param dp 保留小数位
 * @param r 舍入方式，默认向下舍入（round down）
 * @returns string
 */
export function toFixed(num: any, dp: number = 0, r?: RoundingMode): string {
  // 临时提高 Big.DP 以确保计算精度，避免被默认的 Big.DP = 2 限制
  const oldDP = Big.DP;
  if (dp > oldDP) {
    Big.DP = dp;
  }
  const big = new Big(toNum(num));
  const oldRM = Big.RM;
  // 如果指定了 dp 但没有指定 r，默认使用 ROUND_DOWN
  Big.RM = r !== undefined ? r : ROUND_DOWN;
  const result = big.toFixed(dp);
  Big.RM = oldRM;
  Big.DP = oldDP; // 恢复原始 Big.DP
  return result;
}

/**
 * 分组
 * @param numStr 数字字符串
 * @param sep 分隔符
 * @returns string
 */
export function grouping(numStr: string, sep: string = ','): string {
  return numStr.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
}

/**
 * 整数部分分组
 * @param numStr 数字字符串
 * @param sep 分隔符
 * @returns string
 */
export function integerGrouping(numStr: string, sep: string = ','): string {
  return numStr
    .split('.')
    .map((n, i) => (i === 0 ? grouping(n, sep) : n))
    .join('.');
}

/**
 * 格式化展示数字（保留小数位、千分位分隔符）
 * @param num 数值
 * @param dp 保留小数位，默认原小数位
 * @param r 舍入方式，默认向下舍入（round down）
 * @param sep 千分位分隔符，默认,
 * @returns string
 */
export function toFormat(num: any, dp?: number, r?: RoundingMode, sep: string = ','): string {
  const numStr = toFixed(num, dp, r);
  return integerGrouping(numStr, sep);
}

/**
 * 合计
 * @param list 列表
 * @param prop 属性名
 * @param dp 保留小数位，默认原小数位
 * @param r 舍入方式，默认向下舍入（round down）
 * @returns number
 */
export function count<T>(
  list: T[],
  prop?: keyof T,
  dp?: number,
  r?: RoundingMode
): number {
  const sum = list.reduce((result, item) => {
    const value = prop ? (item[prop] as any) : item;
    return add(result, value);
  }, 0);

  return toDP(sum, dp, r);
}

const ordinalSuffixes = ['th', 'st', 'nd', 'rd'];

/**
 * 转化为序数，即英文追加th、st、nd、rd结尾
 * @param num 数值
 * @param locale 语言环境，默认'en'
 * @returns string
 */
export function toOrdinal(num: number, locale: string = 'en'): string {
  const str = num.toString();
  if (!locale.match(/^en(-|$)/i)) return str;

  // 11、12、13固定以th结尾，其他按规则
  if (['11', '12', '13'].includes(str)) {
    return `${str}th`;
  }

  const index = parseInt(str.slice(-1), 10);
  const suffix = ordinalSuffixes[index > 3 ? 0 : index];
  return str + suffix;
}

/**
 * 将百分数转为数值
 * @param percent 百分比数
 * @param dp 保留小数位
 * @param r 舍入方式，默认向零舍入
 * @returns number
 */
export function percent2number(percent: string, dp?: number, r?: RoundingMode): number {
  const str = percent.replace('%', '');
  return div(toNum(str), 100, dp, r);
}

/**
 * Intl.NumberFormat 的兼容性封装
 */
const safeNumberFormat = (
  value: number,
  locale: string,
  options: Intl.NumberFormatOptions
): string => {
  try {
    if (typeof Intl !== 'undefined' && Intl.NumberFormat) {
      return new Intl.NumberFormat(locale, options).format(value);
    }
  } catch (error) {
    console.warn('Intl.NumberFormat not supported, using fallback', error);
  }

  // 降级方案：手动格式化
  const { minimumFractionDigits = 0, maximumFractionDigits = 0 } = options;
  const fixed = Math.max(minimumFractionDigits, maximumFractionDigits);
  const formattedNum = toFixed(value, fixed, ROUND_DOWN);
  // 添加千位分隔符
  const [integerPart, decimalPart] = formattedNum.split('.');
  const withSeparator = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  return decimalPart ? `${withSeparator}.${decimalPart}` : withSeparator;
};

// 获取显示几位小数
const getDecimalDisplay = (numStr: string | number, maxDecimalPart: number = 2) => {
  let bigNum;
  try {
    // 使用 Big.js 确保精度并处理数字
    bigNum = Big(numStr);
  } catch (e) {
    console.log('e-------', e);
    // 捕获无效数字输入
    return -1;
  }

  // Big.js 的 toFixed() 方法可以返回带有指定小数位数的字符串，
  // 但更直接的方法是检查其字符串表示。
  const s = bigNum.toString();

  // 1. 检查是否包含小数点
  const decimalIndex = s.indexOf('.');

  if (decimalIndex === -1) {
    // 整数 (例如: '123')
    return 0;
  }

  // 2. 计算小数部分的长度
  // 注意: Big.js 的 toString() 会自动移除末尾的零，例如 Big('1.100').toString() 是 '1.1'
  const decimalPartLength = s.length - 1 - decimalIndex;

  if (decimalPartLength === 1) {
    // 1 位小数 (例如: '12.3')
    return 1;
  } else if (decimalPartLength >= maxDecimalPart) {
    // 2 位或更多位小数 (例如: '12.34' 或 '12.345')
    return maxDecimalPart;
  }

  // 如果 decimalPartLength === 0，说明是 '1.' 这种形式，Big.js 会处理成整数，
  // 因此这里通常不会被执行到。如果执行到，也应该按整数处理。
  return 0;
};

/**
 * 截断小数，不进行四舍五入
 * @param value 数值
 * @param decimals 保留的小数位数（默认 2）
 * @returns 截断后的数值
 * 
 * @example
 * truncateDecimals(123.456, 2)  // 123.45
 * truncateDecimals(123.999, 2)  // 123.99 (不会变成 124.00)
 * truncateDecimals(123.4, 2)    // 123.40
 */
export const truncateDecimals = (value: number, decimals: number = 2): string => {
  if (value > 0 && value < 0.01) return '0.01';
  // 使用 Big.js 精确截断，避免二进制浮点误差的问题，如2.28 -> 2.27 问题
  const b = new Big(value);
  const truncated = b.round(decimals, ROUND_DOWN);
  // 去掉多余的0和点，如12.00->12，12.10->12.1，12.01->12.01
  return truncated.toFixed(decimals).replace(/\.?0+$/, '');
};

/**
 * 格式化金额，带千万分隔符
 * @param value 
 * @param locale 
 * @param decimals 保留的小数位数（默认 2）
 * @param needMin 是否需要判断小于0.01（默认true）
 * @returns 
 */
export function formatNumberWithCommas(value: number, locale: string, decimals: number = 2, needMin: boolean = true) {
  let num = toNum(value);
  // const region = Env.REGION;
  if (needMin && num > 0 && num < 0.01) return '<0.01';
  num = toNum(truncateDecimals(value, decimals));

  const toFixed = getDecimalDisplay(num);
  // - 若为整数，默认不显示小数；
  // - 若为小数，最多保留2位小数，如果最后1位小数是0，保留1位即可
  return safeNumberFormat(num, locale, {
    minimumFractionDigits: toFixed,
    maximumFractionDigits: toFixed,
    roundingMode: 'trunc',
  });
}